import React, { useCallback, useEffect, useMemo, useState } from "react";
import { pdfjs } from "react-pdf";

import { Box, Paper, useTheme } from "@mui/material";
import DocumentWrapper from "components/Tagger/Document/PDFViewer/DocumentWrapper";
import PDFTopBar from "components/Tagger/Document/PDFViewer/PDFTopBar";
import Rectangle from "components/Tagger/Document/rectangle";
import useDraggableContent from "components/Tagger/Document/useDraggableContent";
import { FormPolygon, IProcessedTag, Nullable, Polygons, Size, TaggerDocument, WordPolygon } from "store/types";

import "./PDFReader.styles.scss";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

export interface PDFReaderProps {
  docId: TaggerDocument["id"];
  file: File;
  error: string | null;
  polygons: Nullable<Polygons>;
  tags: IProcessedTag[];
  page: number | null;
}

interface FormRec {
  children: string[];
  id: string;
  css: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
}

interface WordRec {
  text: string;
  id: string;
  css: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
}

const PDFReader: React.FC<PDFReaderProps> = React.memo(({ docId, file, error, polygons, tags, page }) => {
  const [numPages, setNumPages] = useState<number>(NaN);
  const [pageNumber, setPageNumber] = useState<number>(page || 1);
  const [pageSize, setPageSize] = useState<Size>();
  const [originalScale, setOriginalScale] = useState<number>(1);
  const [userScale, setUserScale] = useState(1);

  const theme = useTheme();

  useDraggableContent(docId.toString());

  useEffect(() => {
    if (!page) {
      setPageNumber(1);
    } else {
      setPageNumber(page);
    }
  }, [file, page]);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
    },
    [setNumPages]
  );

  const [recs, childRecs, formRecs] = useMemo(() => {
    const recs: Record<string, WordRec> = {};
    const childRecs: Set<string> = new Set<string>();
    const formRecs: FormRec[] = [];
    if (polygons?.[pageNumber] && pageSize) {
      polygons[pageNumber].forEach((poly) => {
        const css = {
          left: poly.boundingBox.Left * pageSize.width * originalScale * userScale,
          top: poly.boundingBox.Top * pageSize.height * originalScale * userScale,
          width: poly.boundingBox.Width * pageSize.width * originalScale * userScale,
          height: poly.boundingBox.Height * pageSize.height * originalScale * userScale
        };

        if (poly.hasOwnProperty("text")) {
          recs[poly.id] = {
            text: (poly as WordPolygon).text,
            id: poly.id,
            css: css
          };
        } else {
          formRecs.push({
            children: (poly as FormPolygon).children,
            id: poly.id,
            css: css
          });
          (poly as FormPolygon).children.forEach((id) => childRecs.add(id));
        }
      });
    }

    return [recs, childRecs, formRecs];
  }, [polygons, pageNumber, originalScale, userScale]);

  const taggedRectangles = tags.reduce<Record<string, string>>((acc, tag) => {
    tag.words.forEach((word) => {
      if (!tag.deleted_at) {
        // Don't show deleted tags
        if (!acc[word.rectId] || !tag.is_analysis) {
          // Tags override Suspects
          acc[word.rectId] = tag.measure.name || tag.classification || "";
        }
      }
    });
    return acc;
  }, {});

  const filememo = useMemo(() => file, [file?.name]);

  return (
    <Paper className="document-container">
      <PDFTopBar
        zoom={userScale}
        setZoom={setUserScale}
        loading={!polygons}
        error={error}
        filename={file.name}
        numPages={numPages}
        setPageNumber={(value) => {
          setPageNumber(value);
        }}
        pageNumber={pageNumber}
      />
      <div
        // className="page-container"
        style={{
          maxWidth: "596px",
          margin: "0 auto",
          width: pageSize ? Math.floor(pageSize.width * originalScale) : undefined,
          height: pageSize ? Math.floor(pageSize.height * originalScale) : undefined,
          overflow: "hidden",
          backgroundColor: theme.palette.primary.main
        }}
      >
        <div id={docId.toString()} className="tagger__draggable">
          <DocumentWrapper
            file={filememo}
            pageNumber={pageNumber}
            originalScale={originalScale}
            userScale={userScale}
            setPageSize={setPageSize}
            setOriginalScale={setOriginalScale}
            onDocumentLoadSuccess={onDocumentLoadSuccess}
            isReadOnly
          />
          {Object.keys(recs).map((rectId) => {
            if (!childRecs.has(rectId) && recs[rectId]) {
              return (
                <Rectangle
                  key={rectId}
                  className="form-word"
                  rectangle={recs[rectId]}
                  taggedRectangles={taggedRectangles}
                  tags={tags}
                  selectedWord={null}
                  onClick={() => {}}
                  // onEnter={handleWordEnter}
                />
              );
            }
            return null;
          })}
          {formRecs.map((formRec) => {
            const currentRectangleData = tags.find((tag) =>
              tag.words.some((word) => formRec.children.includes(word.rectId))
            );
            return (
              <Box
                key={formRec.id}
                sx={{
                  position: "absolute",
                  padding: "1px",
                  transform: "translate(-2px, -2px)",
                  boxSizing: "content-box",
                  border: "1px solid gold",
                  zIndex: "1",
                  ...(currentRectangleData && {
                    background: "#07a27d33"
                  }),
                  ...formRec.css
                }}
              >
                {formRec.children.map((recId: string) =>
                  recs[recId] ? (
                    <Rectangle
                      key={recId}
                      taggedRectangles={taggedRectangles}
                      rectangle={recs[recId]}
                      anchorCSS={formRec.css}
                      tags={tags}
                      selectedWord={null}
                      // onEnter={handleWordEnter}
                      onClick={() => {}}
                    />
                  ) : null
                )}
              </Box>
            );
          })}
        </div>
      </div>
    </Paper>
  );
});

export default PDFReader;
